import React, { useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { EntradaCalculationDialog } from './EntradaCalculationDialog';

const requiredEntradaFields = [
    { label: 'Fecha', isMissing: (entrada) => !entrada.fecha },
    { label: 'Almacen', isMissing: (entrada) => !entrada.id_almacen },
    { label: 'Variedad', isMissing: (entrada) => !entrada.codigo_variedad },
    { label: 'Kilos', isMissing: (entrada) => entrada.kilos === null || entrada.kilos === 0 },
    { label: 'Peso del palot', isMissing: (entrada) => !entrada.id_peso_palot },
    { label: 'Peso caja', isMissing: (entrada) => !entrada.id_peso_caja },
];

export const getMissingEntradaFields = (entradaPayload) =>
    requiredEntradaFields
        .filter((field) => field.isMissing(entradaPayload))
        .map((field) => field.label);

export const EntradaActions = ({
    payload,
    totals,
    onPrint,
    onReset,
    onSubmit,
    addDisabled = false,
    printDisabled = false,
    submitting = false,
}) => {
    const [isCalculationDialogOpen, setIsCalculationDialogOpen] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [resetConfirmationStep, setResetConfirmationStep] = useState(0);

    const handleAddEntrada = async () => {
        const missingFields = getMissingEntradaFields(payload);

        if (missingFields.length > 0) {
            setValidationMessage(`Faltan campos obligatorios: ${missingFields.join(', ')}`);
            return;
        }

        await onSubmit?.(payload);
    };

    const closeResetDialog = () => {
        setResetConfirmationStep(0);
    };

    const confirmResetEntrada = () => {
        onReset?.();
        closeResetDialog();
    };

    const handlePrintEntrada = async () => {
        setPrinting(true);

        try {
            await onPrint?.();
        } catch (error) {
            setValidationMessage(error?.message || 'No se ha podido generar el PDF.');
        } finally {
            setPrinting(false);
        }
    };

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddEntrada}
                        disabled={addDisabled || submitting}
                    >
                        Añadir entrada
                    </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        color="secondary"
                        startIcon={<ListAltOutlinedIcon />}
                        onClick={() => setIsCalculationDialogOpen(true)}
                    >
                        Datos
                    </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        color="secondary"
                        startIcon={<PrintOutlinedIcon />}
                        onClick={handlePrintEntrada}
                        disabled={printDisabled || printing}
                    >
                        {printing ? 'Generando...' : 'Imprimir'}
                    </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineOutlinedIcon />}
                        onClick={() => setResetConfirmationStep(1)}
                    >
                        Eliminar
                    </Button>
                </Grid>
            </Grid>

            {totals && (
                <EntradaCalculationDialog
                    open={isCalculationDialogOpen}
                    onClose={() => setIsCalculationDialogOpen(false)}
                    totals={totals}
                />
            )}

            <Snackbar
                open={Boolean(validationMessage)}
                autoHideDuration={4500}
                onClose={() => setValidationMessage('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="error" variant="filled" onClose={() => setValidationMessage('')}>
                    {validationMessage}
                </Alert>
            </Snackbar>

            <Dialog
                open={resetConfirmationStep > 0}
                onClose={closeResetDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {resetConfirmationStep === 1 ? 'Limpiar entrada' : 'Confirmar limpieza'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {resetConfirmationStep === 1
                            ? 'Se borraran todos los datos introducidos y el formulario volvera a sus valores por defecto.'
                            : 'Esta accion no se puede deshacer. Confirma de nuevo si quieres limpiar la entrada.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={closeResetDialog}>
                        Cancelar
                    </Button>
                    {resetConfirmationStep === 1 ? (
                        <Button color="error" variant="outlined" onClick={() => setResetConfirmationStep(2)}>
                            Continuar
                        </Button>
                    ) : (
                        <Button color="error" variant="contained" onClick={confirmResetEntrada}>
                            Limpiar formulario
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};
